// src/components/recipient/RecipientLoading.tsx
import { motion, AnimatePresence } from "framer-motion";

import { useEffect, useState, useMemo } from "react";
import { LoadingScreenConfig } from "../../types/recipient.types";
import logo_gold from "../../assets/img/logo_gold.png";

interface RecipientLoadingProps {
  config: LoadingScreenConfig;
  onComplete: () => void;
}

export const RecipientLoading: React.FC<RecipientLoadingProps> = ({ config, onComplete }) => {
  const [progress, setProgress] = useState(0);
  const [messageIndex, setMessageIndex] = useState(0);
  const [isFinishing, setIsFinishing] = useState(false);
  const [showBreathInstruction, setShowBreathInstruction] = useState(false);

  // Cinematic progress curve
  useEffect(() => {
    const totalDuration = 11000;
    const frameRate = 50;
    const totalSteps = totalDuration / frameRate;
    const baseIncrement = 100 / totalSteps;

    const timer = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(timer);
          setTimeout(() => {
            setIsFinishing(true);
            setTimeout(onComplete, 1500);
          }, 800);
          return 100;
        }

        let currentIncrement = baseIncrement;
        if (prev < 20) {
          currentIncrement = baseIncrement * 0.7;
        } else if (prev > 70) {
          currentIncrement = baseIncrement * (0.5 - (prev - 70) / 100);
        } else {
          const stepSize = 100 / config.progressMessages.length;
          const isNearMessageChange = prev % stepSize > stepSize - 8;
          if (isNearMessageChange) {
            currentIncrement *= 0.3;
          }
        }

        return Math.min(prev + currentIncrement, 100);
      });
    }, frameRate);

    return () => clearInterval(timer);
  }, [config.progressMessages.length, onComplete]);

  useEffect(() => {
    if (progress > 70 && progress < 85) {
      setShowBreathInstruction(true);
    } else {
      setShowBreathInstruction(false);
    }
  }, [progress]);

  useEffect(() => {
    const step = 100 / config.progressMessages.length;
    const index = Math.min(Math.floor(progress / step), config.progressMessages.length - 1);
    setMessageIndex(index);
  }, [progress, config.progressMessages.length]);

  const bgGradient = useMemo(() => {
    const { theme } = config;
    return `radial-gradient(circle at center, ${theme.primary}22 0%, ${theme.secondary}22 50%, #000 100%)`;
  }, [config]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, scale: 1.05, filter: "blur(20px)" }}
      transition={{ duration: 1.5 }}
      className="fixed inset-0 z-[1000] flex flex-col items-center justify-center overflow-hidden"
      style={{
        background: bgGradient,
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 0.3, y: 0 }}
        transition={{ duration: 2 }}
        className="absolute top-1/3 text-center"
      >
        <p className="text-white/20 text-xs tracking-[0.3em] uppercase">
          Someone prepared something for you
        </p>
      </motion.div>

      <motion.div
        animate={{
          opacity: [0.1, 0.3, 0.1],
          scale: [1, 1.3, 1],
        }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(circle at center, ${config.theme.primary}44 0%, transparent 70%)`,
        }}
      />

      <div className="absolute inset-0 pointer-events-none opacity-40">
        {[...Array(30)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full"
            style={{
              width: i % 3 === 0 ? "4px" : "2px",
              height: i % 3 === 0 ? "4px" : "2px",
              background: i % 2 === 0 ? config.theme.primary : "#fff",
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              filter: "blur(1px)",
            }}
            animate={{
              y: [0, -60, 0],
              opacity: [0, 0.8, 0],
              x: [0, (Math.random() - 0.5) * 40, 0],
            }}
            transition={{
              duration: 8 + Math.random() * 7,
              repeat: Infinity,
              delay: Math.random() * 8,
              ease: "easeInOut",
            }}
          />
        ))}
      </div>

      <div className="relative z-10 flex flex-col items-center max-w-xl px-10 text-center">
        {/* REPLACED ICON WITH LOGO */}
        <motion.div
          animate={{
            y: [0, -10, 0],
            scale: [1, 1.05, 1],
            filter: [
              "brightness(1) drop-shadow(0 0 0px transparent)",
              `brightness(1.2) drop-shadow(0 0 15px ${config.theme.primary}66)`,
              "brightness(1) drop-shadow(0 0 0px transparent)",
            ],
          }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          className="mb-16"
        >
          {/* Replace src with your actual logo path */}
          <img
            src={logo_gold}
            alt="Logo"
            className="h-16 w-auto object-contain"
            onError={(e) => {
              // Fallback if image fails to load
              e.currentTarget.style.display = "none";
            }}
          />
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 1.2 }}
          className="text-xl md:text-4xl font-light tracking-tight text-white/90 mb-12 leading-relaxed"
        >
          {config.message}
        </motion.h2>

        <div className="h-20 flex items-center justify-center">
          <AnimatePresence mode="wait">
            <motion.p
              key={messageIndex}
              initial={{ opacity: 0, y: 10, filter: "blur(4px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: -10, filter: "blur(4px)" }}
              transition={{ duration: 0.8 }}
              className="text-white/50 text-xs md:text-sm font-medium tracking-[0.3em] uppercase"
              style={{ color: config.theme.primary + "cc" }}
            >
              {config.progressMessages[messageIndex]}
            </motion.p>
          </AnimatePresence>
        </div>

        <motion.div
          initial={false}
          animate={{
            opacity: showBreathInstruction ? 1 : 0,
            height: showBreathInstruction ? "auto" : 0,
            scale: showBreathInstruction ? 1 : 0.95,
            marginTop: showBreathInstruction ? 24 : 0,
            marginBottom: showBreathInstruction ? 16 : 0,
          }}
          transition={{
            duration: 0.6,
            ease: [0.22, 1, 0.36, 1],
          }}
          className="overflow-hidden"
        >
          <p className="text-white/30 text-[10px] tracking-[0.4em] uppercase italic">
            Take a breath...
          </p>
        </motion.div>

        <div className="mt-20 w-72 md:w-96">
          <div className="flex justify-between items-end mb-3">
            <motion.span
              animate={{ opacity: [0.2, 0.5, 0.2] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="text-[8px] font-black uppercase tracking-[0.4em] text-white/30"
            >
              {isFinishing ? "Ready" : "Preparing"}
            </motion.span>
            <span className="text-[9px] font-mono text-white/20">
              {Math.min(Math.round(progress), 100)}%
            </span>
          </div>

          <div className="h-[2px] w-full bg-white/10 relative">
            <motion.div
              className="absolute top-0 left-0 h-full"
              style={{
                width: `${progress}%`,
                background: `linear-gradient(90deg, ${config.theme.primary}, ${config.theme.secondary})`,
              }}
              transition={{ type: "spring", damping: 25, stiffness: 40 }}
            />
            <motion.div
              className="absolute top-[-4px] h-[10px] w-[10px] rounded-full blur-md"
              style={{
                left: `${progress}%`,
                backgroundColor: config.theme.primary,
              }}
            />
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.2 }}
        transition={{ delay: 2 }}
        className="absolute bottom-12"
      >
        <span className="text-[8px] uppercase tracking-[0.8em] text-white font-light">
          A Love Moment by LRNSY
        </span>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.15 }}
        transition={{ delay: 3 }}
        className="absolute bottom-24 text-[10px] tracking-widest"
        style={{ color: config.theme.primary }}
      >
        from someone who chose you
      </motion.div>
    </motion.div>
  );
};
